import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from "@mui/material";
import {
  Alarm,
  GridView,
  GroupsOutlined,
  InventoryOutlined,
  LogoutOutlined,
  PieChartOutline,
  SettingsOutlined,
  ShoppingCartOutlined,
  ShowChart,
  StorefrontOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";
import { useIsAdminMode, useSettings } from "@/contexts";
import { AdminPermissions } from "@/enums";
import { AdminData } from "@/interfaces";
import { AdminRoute } from "@/components/atoms";
import {
  LoginAsAdminDialog,
  OrderEditForm,
  InventoryCountEditForm,
  clearAdminCredentials,
  getStoredToken,
  ChangePasswordDialog,
} from "@/components/organisms";
import Overview from "../Overview";
import Products from "../Products";
import Orders from "../Orders";
import ShippingPortal from "../ShippingPortal";
import Dealers from "../Dealers";
import Reporting from "../Reporting";
import History from "../History";
import InventoryCounts from "../InventoryCounts";
import Settings from "../Settings";
import NoMatch from "../NoMatch";
import { colors } from "@/theme";
import { SettingsHelper } from "@/helpers";
import AdminSidebarLogo from '@/assets/admin_sidebar_logo.svg';
import productSvg from '@/assets/product_svg.svg';
import order from '@/assets/orders_svg.svg';
import shippingPortal from '@/assets/shippingPortal_svg.svg';
import attendees from '@/assets/attendees_svg.svg';
import reporting from '@/assets/reporting_svg.svg';
import inventory from '@/assets/inventory_svg.svg';
import settings from '@/assets/settings_svg.svg';
import logout from '@/assets/logout_svg.svg';
import * as S from './styles';
import TestShippingPortal from "../Test Shipping Portal";
import TestInventoryCounts from "../TestInventoryCounts";
import InventoryCountEditFormTest from "@/components/organisms/InventoryCountEditFormTest";

const drawerWidth = 240;

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdminMode, setIsAdminMode, currentAdminData, setCurrentAdminData } =
    useIsAdminMode();
  const { loadSettings } = useSettings();
  const [isLoginAsAdminDialogOpen, setIsLoginAsAdminDialogOpen] = useState(
    !isAdminMode
  );
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [isValidatingSession, setIsValidatingSession] = useState<boolean>(
    () => isAdminMode && !!getStoredToken()
  );

  useEffect(() => {
    const token = getStoredToken();

    if (!token) {
      if (isAdminMode) {
        clearAdminCredentials();
        setCurrentAdminData(undefined);
        setIsAdminMode(false);
        setIsLoginAsAdminDialogOpen(true);
      }
      return;
    }

    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_REST_BASE_URL}/User/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (cancelled) return;
        if (response.data && response.data.userId) {
          setCurrentAdminData({
            userId: response.data.userId,
            email: response.data.email,
            permissions: response.data.permission,
            token,
          });
        } else {
          clearAdminCredentials();
          setCurrentAdminData(undefined);
          setIsAdminMode(false);
          setIsLoginAsAdminDialogOpen(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        clearAdminCredentials();
        setCurrentAdminData(undefined);
        setIsAdminMode(false);
        setIsLoginAsAdminDialogOpen(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsValidatingSession(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPermissions = currentAdminData?.permissions;
  const canViewProducts = SettingsHelper.hasProductsPermission(currentPermissions);
  const canViewOrders = SettingsHelper.hasOrdersPermission(currentPermissions);
  const canViewShippingPortal =
    SettingsHelper.hasShippingPortalPermission(currentPermissions);
  const canViewAttendees = SettingsHelper.hasAttendeesPermission(currentPermissions);
  const canViewReporting = SettingsHelper.hasReportingPermission(currentPermissions);
  const canViewInventoryCounts =
    SettingsHelper.hasInventoryCountsPermission(currentPermissions);
  const canViewSettings = SettingsHelper.hasSettingsPermission(currentPermissions);

  const onLoginAsAdminDialogClose = (
    _isAdmin?: boolean,
    adminData?: AdminData
  ) => {
    if (!_isAdmin) {
      return;
    }

    setIsAdminMode(true);
    setCurrentAdminData(adminData);
    setIsLoginAsAdminDialogOpen(false);
    
    // Load settings after successful login
    loadSettings().catch((error) => {
      console.error("Failed to load settings after login:", error);
    });
    
    // Only navigate to products if we're at the root /admin path
    // Otherwise stay on the current page (for refreshes)
    if (location.pathname === "/admin" || location.pathname === "/admin/") {
      navigate("products");
    }
  };

  const onButtonLogoutClick = () => {
    // Clear stored credentials
    clearAdminCredentials();
    
    // Clear admin data first, then set admin mode to false
    setCurrentAdminData(undefined);
    setIsAdminMode(false);
    setIsLoginAsAdminDialogOpen(true);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <S.SidebarDrawer variant="permanent" anchor="left">
        <S.SidebarToolbar />
        <S.LogoBox>
          <S.LogoImage src={AdminSidebarLogo} alt="DIRECTV Revolution Logo" />
        </S.LogoBox>
        <S.SidebarList>
          {/* <ListItemButton 
            component={Link} 
            to="/admin/overview"
            selected={location.pathname === "/admin/overview"}
          >
            <S.SidebarListItemIcon>
              <ShowChart />
            </S.SidebarListItemIcon>
            <ListItemText primary="Overview" />
          </ListItemButton> */}
          {canViewProducts && (
            <ListItemButton
              component={Link}
              to="/admin/products"
              selected={location.pathname.includes("/admin/products")}
            >
              <S.SidebarListItemIcon>
                <img src={productSvg} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Products" />
            </ListItemButton>
          )}
          {canViewOrders && (
            <ListItemButton
              component={Link}
              to="/admin/orders"
              selected={location.pathname.includes("/admin/orders")}
            >
              <S.SidebarListItemIcon>
                <img src={order} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Orders" />
            </ListItemButton>
          )}
          {canViewShippingPortal && (
            <ListItemButton
              component={Link}
              to="/admin/shippingportal"
              selected={location.pathname.includes("/admin/shippingportal")}
            >
              <S.SidebarListItemIcon>
                <img src={shippingPortal} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Shipping Portal" />
              {/* </ListItemButton> <ListItemButton 
              component={Link} 
              to="/admin/test"
              selected={location.pathname === "/admin/test"}
            >
              <S.SidebarListItemIcon>
                <StorefrontOutlined />
              </S.SidebarListItemIcon>
              <ListItemText primary="Shipping Portal" /> */}
            </ListItemButton>
          )}
          {canViewAttendees && (
            <ListItemButton
              component={Link}
              to="/admin/attendees"
              selected={location.pathname.includes("/admin/attendees")}
            >
              <S.SidebarListItemIcon>
                <img src={attendees} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Attendees" />
            </ListItemButton>
          )}
          {canViewReporting && (
            <ListItemButton
              component={Link}
              to="/admin/reporting"
              selected={location.pathname.includes("/admin/reporting")}
            >
              <S.SidebarListItemIcon>
                <img src={reporting} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Reporting" />
            </ListItemButton>
          )}
          {canViewSettings && (
            <ListItemButton
              component={Link}
              to="/admin/history"
              selected={location.pathname.includes("/admin/history")}
            >
              <S.SidebarListItemIcon>
                <Alarm />
              </S.SidebarListItemIcon>
              <ListItemText primary="History" />
            </ListItemButton>
          )}
          <S.SidebarDivider />
          {canViewInventoryCounts && (
            <ListItemButton
              component={Link}
              to="/admin/inventorycounts"
              selected={location.pathname.includes("/admin/inventorycounts")}
            >
              <S.SidebarListItemIcon>
                <img src={inventory} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Inventory Counts" />
            </ListItemButton>
          )}


          {/* <ListItemButton
            component={Link}
            to="/admin/testinventorycounts"
            selected={location.pathname.includes("/admin/testinventorycounts")}
          >
            <S.SidebarListItemIcon>
              <InventoryOutlined />
            </S.SidebarListItemIcon>
            <ListItemText primary="Test Inv Counts" />
          </ListItemButton> */}



          {canViewSettings && (
            <ListItemButton
              component={Link}
              to="/admin/settings"
              selected={location.pathname.includes("/admin/settings")}
            >
              <S.SidebarListItemIcon>
                <img src={settings} alt="Products" style={{ width: 24, height: 24 }} />
              </S.SidebarListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          )}
          <ListItemButton onClick={() => setIsChangePasswordDialogOpen(true)}>
            <S.SidebarListItemIcon>
              <VpnKeyOutlined style={{ width: 24, height: 24 }} />
            </S.SidebarListItemIcon>
            <ListItemText primary="Change Password" />
          </ListItemButton>
          <ListItemButton onClick={onButtonLogoutClick}>
            <S.SidebarListItemIcon>
              <img src={logout} alt="Products" style={{ width: 24, height: 24 }} />
            </S.SidebarListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </S.SidebarList>
      </S.SidebarDrawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: "background.default" }}
      >
        {isValidatingSession ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
        <Routes>
          <Route
            index
            element={
              isAdminMode ? (
                <Navigate to="products" replace />
              ) : (
                <Box />
              )
            }
          />
          {/* <Route
            path="overview"
            element={
              <AdminRoute isAdminMode={isAdminMode}>
                <Overview />
              </AdminRoute>
            }
          /> */}
          <Route
            path="products"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.PRODUCTS}
              >
                <Products />
              </AdminRoute>
            }
          />
          <Route
            path="orders"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.ORDERS}
              >
                <Orders />
              </AdminRoute>
            }
          />
          <Route
            path="orders/:orderId"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.ORDERS}
              >
                <OrderEditForm />
              </AdminRoute>
            }
          />
          <Route
            path="shippingportal"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.SHIPPING_PORTAL}
              >
                <ShippingPortal />
              </AdminRoute>
            }
          />
          {/* <Route
            path="test"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.SHIPPING_PORTAL}
              >
                <TestShippingPortal />
              </AdminRoute>
            }
          /> */}
          <Route
            path="attendees"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.ATTENDEES}
              >
                <Dealers />
              </AdminRoute>
            }
          />
          <Route
            path="reporting"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.REPORTING}
              >
                <Reporting />
              </AdminRoute>
            }
          />
          <Route
            path="history"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.SETTINGS}
              >
                <History />
              </AdminRoute>
            }
          />
          <Route
            path="inventorycounts"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.INVENTORY_COUNTS}
              >
                <TestInventoryCounts />
              </AdminRoute>
            }
          />

          {/* <Route
            path="testinventorycounts"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.INVENTORY_COUNTS}
              >
                <TestInventoryCounts />
              </AdminRoute>
            }
          /> */}

          {/* <Route
            path="testinventorycounts/:inventoryCountId"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.INVENTORY_COUNTS}
              >
                <InventoryCountEditFormTest />
              </AdminRoute>
            }
          /> */}

          <Route
            path="inventorycounts/:inventoryCountId"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.INVENTORY_COUNTS}
              >
                <InventoryCountEditForm />
              </AdminRoute>
            }
          />
          <Route
            path="settings"
            element={
              <AdminRoute
                isAdminMode={isAdminMode}
                currentPermissions={currentAdminData?.permissions}
                requiredPermission={AdminPermissions.SETTINGS}
              >
                <Settings />
              </AdminRoute>
            }
          />
          <Route
            path="*"
            element={
              <NoMatch message="This Section Is for Authorized Users Only" />
            }
          />
        </Routes>
        )}
      </Box>
      <LoginAsAdminDialog
        open={isLoginAsAdminDialogOpen}
        onClose={onLoginAsAdminDialogClose}
      />
      <ChangePasswordDialog
        open={isChangePasswordDialogOpen}
        onClose={() => setIsChangePasswordDialogOpen(false)}
      />
    </Box>
  );
};

export default Admin;
